import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { LegalDoc } from '../../data/legalDocs';

interface LegalCardProps {
  doc: LegalDoc;
  index: number;
}

export const LegalCard: React.FC<LegalCardProps> = ({ doc, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  const IconComponent = doc.icon;

  const handleClick = () => {
    window.location.href = doc.href;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{
        duration: 0.5,
        delay: index * 0.1,
        ease: [0.25, 0.1, 0.25, 1],
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <motion.button
        onClick={handleClick}
        className="group relative w-full text-left p-6 rounded-2xl bg-white/[0.04] backdrop-blur-sm border border-white/[0.08] cursor-pointer overflow-hidden transition-all duration-250 hover:border-[#28e675]/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#28e675]/50"
        style={{
          boxShadow: '0 18px 70px rgba(0, 0, 0, 0.55)',
        }}
        whileHover={{
          y: -2,
        }}
        transition={{ duration: 0.2 }}
      >
        {/* Subtle glow on hover */}
        <motion.div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: isHovered
              ? 'radial-gradient(circle at 50% 0%, rgba(40, 230, 117, 0.04) 0%, transparent 70%)'
              : 'transparent',
          }}
          transition={{ duration: 0.3 }}
        />

        <div className="relative flex items-start gap-4">
          {/* Icon */}
          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-white/[0.05] border border-white/[0.08] flex items-center justify-center text-white/60 group-hover:text-[#28e675]/80 group-hover:border-[#28e675]/20 transition-all duration-300">
            <IconComponent className="w-6 h-6" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-white/90 group-hover:text-white mb-1 transition-colors duration-200">
              {doc.title}
            </h3>
            {doc.description && (
              <p className="text-sm text-white/50 mb-2 leading-relaxed">
                {doc.description}
              </p>
            )}
            <p className="text-xs text-white/30 font-medium">
              Dernière mise à jour: {doc.updatedAt}
            </p>
          </div>

          {/* Chevron */}
          <motion.div
            className="flex-shrink-0 text-white/40 group-hover:text-[#28e675]/70 transition-colors duration-200"
            animate={{
              x: isHovered ? 2 : 0,
            }}
            transition={{ duration: 0.2 }}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </motion.div>
        </div>
      </motion.button>
    </motion.div>
  );
};
